/**
 * Groups API — typed client wrapper for the `group.*` oRPC namespace.
 *
 * This is the INTEGRATION SEAM between the family-groups web surfaces (track
 * G-C) and the `group.*` backend namespace (track G-B). The backend agent is
 * building the router to the contract in
 * `.plan/features/2026-07-02-family-groups.md §4.4`; this module mirrors that
 * contract with narrow, hand-written types so the web tree compiles and can be
 * unit-tested against a mock BEFORE the generated router types land in this
 * worktree.
 *
 * ── One-line swap when the generated client lands ──────────────────────────
 * Every call currently routes through `groupNs()` / `recipeNs()`, which cast
 * the shared oRPC client to `any` and invoke `client.group.<proc>` /
 * `client.recipe.<proc>`. When `AppRouter` gains the `group` namespace (and the
 * `sourceGroupShare` arm on `recipe.spinOff`), delete the `as any` casts in
 * those two helpers — the RouterClient types then flow through unchanged and any
 * contract drift becomes a compile error here and at every call site.
 *
 * ── Auth / transport ────────────────────────────────────────────────────────
 * Reuses `createApiClient` (credentials: include + CSRF double-submit), so
 * every group mutation rides the session cookie. No new transport.
 *
 * Refs:
 *   .plan/features/2026-07-02-family-groups.md §4.4 (API surface), §3.4 (perms)
 *   branding.md §8 — "Spin-off" never "fork"; "Variation" never "branch".
 */

import { createApiClient, type ApiClient } from "@proof/client-core/api";

// ── Contract types (plan §4.4) ────────────────────────────────────────────────
//
// WRAPPER DECISIONS (documented for the G-B backend agent to align to):
//   • `role` is the two-value union `owner | member` (plan §3.2).
//   • Timestamps are Unix-ms integers (matches the DO/D1 `*_at` columns).
//   • Handles are bare (no leading `@`); the UI adds the `@` prefix on render
//     per branding.md §8 (`@handle` is the canonical inline form).
//   • `group.list` returns `{ groups: GroupSummary[] }` (envelope, not a bare
//     array) so the shape can grow without a client break.
//   • `group.get` returns meta + members inline (one round trip for the page).
//   • `group.shares.get` returns the full frozen snapshot incl. `body` markdown.

export type GroupRole = "owner" | "member";

/** One row of `group.list` — a membership the caller holds. */
export interface GroupSummary {
  uuid: string;
  name: string;
  role: GroupRole;
  /** Member count (D1 COUNT) — drives the quiet "· 4 members" subtitle. */
  memberCount: number;
  /**
   * Downgrade safety (WP-A/WP-B): server-computed read-only state, resolved for
   * the whole list in one batched query. True when the group's OWNER is over
   * their owned-groups cap and this group falls outside their earliest-created
   * N — the group stays fully readable, but every headroom-consuming write
   * (invite / approve / share-into / rename / re-describe / assign moderator)
   * refuses. Keyed on the owner, never the caller, so a member sees the same
   * state the owner does. The boolean is the ONLY thing that crosses the wire:
   * the owner's tier, cap, and owned-count stay server-side.
   */
  frozen: boolean;
}

/** A member row inside `group.get`. */
export interface GroupMember {
  userUuid: string;
  /** Bare handle (no `@`). May be null if a member never set one. */
  handle: string | null;
  role: GroupRole;
  joinedAt: number;
}

/** `group.get` — group meta + member roster (member-gated). */
export interface GroupMeta {
  uuid: string;
  name: string;
  /** The CALLER's role in this group — gates owner-only controls. */
  role: GroupRole;
  ownerUuid: string;
  members: GroupMember[];
  /**
   * Downgrade safety (WP-A/WP-B): server-computed read-only state for this
   * group. See {@link GroupSummary.frozen} — same server gate, same
   * owner-keyed semantics, resolved on `group.get`.
   */
  frozen: boolean;
  /** B-WP12: public posture. 'private' (default) = member-only, off the directory. */
  visibility: "private" | "public";
  /** B-WP12: the directory blurb (owner-editable). Null when unset. */
  description: string | null;
  /** B-WP12: user_uuids holding the moderator overlay grant. */
  moderatorUuids: string[];
  /** B-WP12: true when the CALLER is a moderator (not owner). */
  isModerator: boolean;
  /** B-WP12: true when the caller can moderate (owner OR moderator). */
  canModerate: boolean;
}

/** One row of `group.directory.list` — a public directory card (B-WP12). */
export interface DirectoryGroup {
  groupUuid: string;
  name: string;
  description: string | null;
  /** Display-only; NEVER a ranking input (⚑ Decision 3). */
  memberCount: number;
}

/** `group.publicGet` — a public group's public meta + viewer relationship. */
export interface PublicGroupMeta {
  groupUuid: string;
  name: string;
  description: string | null;
  memberCount: number;
  viewerStatus: "none" | "member" | "pending" | "approved" | "declined";
}

/** One moderator row (`group.moderators.list`). */
export interface GroupModerator {
  userUuid: string;
  handle: string | null;
  grantedAt: number;
}

/** One row of `group.shares.list` — a card on the group page. */
export interface GroupShareSummary {
  shareUuid: string;
  recipeUuid: string;
  ownerUuid: string;
  /** Bare handle (no `@`). */
  ownerHandle: string;
  title: string;
  slug: string;
  emoji?: string | null;
  /** Unix-ms; drives the "· 3d ago" relative line. */
  sharedAt: number;
  /**
   * Headline rating of the snapshotted go-to attempt (/5, one decimal),
   * derived server-side at share time. ABSENT when the shared body carries no
   * rating (the server omits the field rather than sending null). The
   * explicit `| undefined` mirrors the zod-inferred wire type under
   * exactOptionalPropertyTypes.
   */
  rating?: number | undefined;
  /**
   * B-WP1b: live cook-report aggregate on this share — total delivered reports
   * and the distinct member count. Drives the card's quiet "Cooked N times by M
   * members" secondary line. Always a number (server defaults to 0), so the
   * card branches on `> 0` without a null guard.
   */
  reportsCount: number;
  reportersCount: number;
  /**
   * B-WP2: live notes count on this share. Drives the card's quiet "N notes"
   * secondary line. Always a number (server defaults to 0).
   */
  notesCount: number;
}

/** `group.shares.get` — the full frozen snapshot for the read view. */
export interface GroupShareFull extends GroupShareSummary {
  /** Frozen canonical markdown (server projection output). */
  body: string;
  attemptUuid: string;
  /** True when the caller owns this share (gates Update/Unshare). */
  isOwnShare: boolean;
}

/**
 * One delivered cook report on a group share (`group.reports.list`, B-WP1a).
 * rating / noteExcerpt / cookedAt are SERVER-DERIVED from the reporter's frozen
 * attempt — never client-supplied. `message` is the reporter's optional post
 * message (≤280). `cookedAt` is the freeze timestamp; `deliveredAt` the moment
 * it reached this group — they diverge on a late "Finish the log" share, which
 * the row surfaces as "cooked <weekday> · shared today".
 */
export interface CookReport {
  reportUuid: string;
  shareUuid: string;
  recipeUuid: string;
  reporterUuid: string;
  /** Bare handle (no `@`); the UI adds the prefix on render. */
  reporterHandle: string;
  attemptUuid: string;
  /** /5-normalised headline rating; null when the frozen body carries none. */
  rating: number | null;
  noteExcerpt: string | null;
  message: string | null;
  photoMediaKey: string | null;
  cookedAt: number;
  deliveredAt: number;
}

/**
 * Per-group outcome of `group.reports.publish` (B-WP1a). NEVER a silent partial
 * drop (Rule 8) — every target group returns an explicit status:
 *   • `published`  — the report is live on the group's anchored share.
 *   • `not_member` — the caller isn't in that group.
 *   • `no_anchor`  — the recipe isn't shared to that group (drives ask-to-share).
 *   • `log_full`   — the group's report log is at cap.
 *   • `error`      — an infrastructure failure for that group only.
 * `message` carries voice-reviewed copy for the surfaced non-success outcomes.
 */
export interface PublishReportOutcome {
  groupUuid: string;
  status: "published" | "not_member" | "no_anchor" | "log_full" | "error";
  // `| undefined` (not bare optional) to line up with the generated router
  // type under exactOptionalPropertyTypes.
  reportUuid?: string | undefined;
  shareUuid?: string | undefined;
  message?: string | undefined;
}

/**
 * The friends-audience outcome of `group.reports.publish` (B-WP11b) — sits
 * beside the per-group outcomes so a friends failure is named, never silent
 * (Rule 8). `delivered` even at 0 friends (share intent recorded; F4 = no
 * backfill). `error` = a compensated fan-out failure to surface.
 */
export interface FriendsReportOutcome {
  status: "delivered" | "error";
  delivered?: number | undefined;
  message?: string | undefined;
}

/**
 * One note on a group share (`group.notes.list`, B-WP2). Single-level,
 * group-scoped. `body` is the raw note text (≤2000). Hard-deleted, no tombstones.
 */
export interface GroupNote {
  noteUuid: string;
  shareUuid: string;
  authorUuid: string;
  /** Bare handle (no `@`); the UI adds the prefix on render. */
  authorHandle: string;
  body: string;
  createdAt: number;
}

/** The four group activity event kinds (B-WP3). */
export type GroupEventKind = "recipe_shared" | "share_updated" | "cook_report" | "member_joined";

/**
 * One row of the group activity ledger (`group.events.list`, B-WP3). A quiet
 * append-only record: newest-first, capped to the most recent 200 server-side.
 */
export interface GroupEvent {
  eventUuid: string;
  kind: GroupEventKind;
  actorUuid: string;
  /** Bare handle (no `@`); may be "" for a member who has no handle yet. */
  actorHandle: string;
  /** Recipe title for share / cook events; null for member_joined. */
  subjectTitle: string | null;
  subjectShareUuid: string | null;
  createdAt: number;
}

/** One row of `group.myShares` — a group this recipe is already shared into. */
export interface MyShareRow {
  groupUuid: string;
  name: string;
  shareUuid: string;
  sharedAt: number;
}

/**
 * The caller's relationship to a group reached via an invite token.
 *   • `none`     — not a member, no request pending → show "Ask to join".
 *   • `pending`  — request already submitted → show "Request sent".
 *   • `approved` — request approved but membership not yet present (edge case:
 *                  approved-then-left); the join route treats this as "Ask to
 *                  join" so the user can re-request.
 *   • `declined` — request was declined → the user may re-request.
 *   • `member`   — already in the group → route straight to the group page.
 *
 * Mirrors the server's `group_join_requests.status` union verbatim (the
 * membership check runs first and collapses an active membership to `member`).
 */
export type JoinStatus = "none" | "pending" | "approved" | "declined" | "member";

/** Token → group-name + caller relationship (drives the join route render). */
export interface InvitePreview {
  groupUuid: string;
  name: string;
  status: JoinStatus;
}

/** A pending join request in the owner's REQUESTS section (`group.requests.list`). */
export interface JoinRequest {
  userUuid: string;
  /** Bare handle (no `@`). */
  handle: string | null;
  requestedAt: number;
}

// ── The typed surface ──────────────────────────────────────────────────────────

export interface GroupsApi {
  create(input: { name: string }): Promise<{ uuid: string }>;
  list(): Promise<{ groups: GroupSummary[] }>;
  get(input: { groupUuid: string }): Promise<GroupMeta>;
  rename(input: { groupUuid: string; name: string }): Promise<void>;
  // group.delete — named `remove` on the wrapper (`delete` is an awkward method name).
  remove(input: { groupUuid: string }): Promise<void>;
  leave(input: { groupUuid: string }): Promise<void>;
  removeMember(input: { groupUuid: string; userUuid: string }): Promise<void>;
  mintInvite(input: { groupUuid: string }): Promise<{ url: string }>;
  revokeInvite(input: { groupUuid: string; tokenHash: string }): Promise<void>;
  // ── Request → approve join flow (operator amendment, plan a3e9696) ──────────
  /**
   * Resolve a raw invite token to the group name + the caller's current
   * relationship, WITHOUT creating a request. Drives the join route's first
   * render and its pending-status persistence on revisit.
   *
   * WRAPPER DECISION (flag for G-B): the amended contract lists
   * `group.join.request`, `group.requests.list`, `group.requests.approve` and
   * `.decline`, but the join route must show `<group name>` before the user
   * acts AND show a persisted "Request sent" state on revisit — both need a
   * read-only token→{name,status} resolve. Maps to `group.invite.preview`
   * (member-safe, no side effects). If G-B folds this into `group.join.request`
   * as an idempotent read-then-write, collapse the two wrapper methods.
   */
  previewInvite(input: { token: string }): Promise<InvitePreview>;
  /** `group.join.request` — submit a join request (owner approves later). */
  requestToJoin(input: { token: string }): Promise<InvitePreview>;
  /** `group.requests.list` — owner-only pending requests. */
  listRequests(input: { groupUuid: string }): Promise<{ requests: JoinRequest[] }>;
  /** `group.requests.approve` — owner accepts; requester becomes a member. */
  approveRequest(input: { groupUuid: string; userUuid: string }): Promise<void>;
  /** `group.requests.decline` — owner rejects the request. */
  declineRequest(input: { groupUuid: string; userUuid: string }): Promise<void>;
  share(input: {
    groupUuid: string;
    recipeUuid: string;
  }): Promise<{ shareUuid: string; groupUuid: string }>;
  unshare(input: { groupUuid: string; recipeUuid: string }): Promise<void>;
  listShares(input: { groupUuid: string }): Promise<{ shares: GroupShareSummary[] }>;
  getShare(input: { groupUuid: string; shareUuid: string }): Promise<GroupShareFull>;
  /**
   * Extends the existing `recipe.spinOff` with the `sourceGroupShare` arm
   * (plan §3.3). Returns the new recipe's uuid + slug for the landing redirect.
   */
  spinOffFromGroupShare(input: {
    groupUuid: string;
    shareUuid: string;
  }): Promise<{ uuid: string; slug: string }>;
  /**
   * `group.myShares` — the caller's own outbound share registry for one recipe.
   * B-WP1b uses it to seed the log-a-cook sheet's `hasRecipeShare` per group so
   * the ask-to-share prompt only appears for a group that has no share yet.
   */
  myShares(input: { recipeUuid: string }): Promise<{ shares: MyShareRow[] }>;
  /**
   * `group.reports.publish` — deliver a cook report to one or more groups.
   * Per-group outcomes are returned; a partial failure is never silent (Rule 8).
   * rating / note / cookedAt are server-derived — the input carries none.
   */
  publishReports(input: {
    recipeUuid: string;
    variationUuid: string;
    attemptUuid: string;
    groupUuids: string[];
    // B-WP11b — the friends audience bit. When true, the SAME publish path fans
    // out one feed_items row per active friend. `groupUuids` may be empty on a
    // friends-only share.
    friends?: boolean;
    message?: string;
  }): Promise<{ outcomes: PublishReportOutcome[]; friends?: FriendsReportOutcome | undefined }>;
  /** `group.reports.retract` — remove the caller's own report from one group. */
  retractReport(input: { attemptUuid: string; groupUuid: string }): Promise<void>;
  /** `group.reports.list` — delivered cook reports on one share (member-gated). */
  listReports(input: { groupUuid: string; shareUuid: string }): Promise<{ reports: CookReport[] }>;
  /**
   * `group.reports.anchors` — for each group, whether this recipe already has an
   * anchor there (a share of the recipe OR, one upstream hop, of its spin-off
   * source). B-WP1b seeds the log-a-cook sheet's ask-to-share prompt off this:
   * the prompt appears ONLY for a group with no anchor, so a cook of a spin-off
   * whose upstream is group-shared publishes straight onto the existing share
   * rather than spawning a duplicate share of the spin-off (B1 / acceptance
   * test 1). Same resolution the publish path uses, so the seed never disagrees.
   */
  reportAnchors(input: {
    recipeUuid: string;
    groupUuids: string[];
  }): Promise<{ anchors: Array<{ groupUuid: string; hasAnchor: boolean }> }>;
  // ── Notes on shares (B-WP2) ─────────────────────────────────────────────────
  /** `group.notes.add` — add a note to a share (≤2000 chars, member-gated). */
  addNote(input: {
    groupUuid: string;
    shareUuid: string;
    body: string;
  }): Promise<{ noteUuid: string; createdAt: number }>;
  /** `group.notes.delete` — author or group owner removes a note. */
  deleteNote(input: { groupUuid: string; noteUuid: string }): Promise<void>;
  /**
   * `group.notes.report` — flag a note to the operator (routes to the global
   * content_reports queue, never GitHub — D-8.1). Idempotent per open report.
   */
  reportNote(input: {
    groupUuid: string;
    shareUuid: string;
    noteUuid: string;
  }): Promise<{ alreadyReported: boolean }>;
  /** `group.notes.list` — notes on one share, oldest-first (member-gated). */
  listNotes(input: { groupUuid: string; shareUuid: string }): Promise<{ notes: GroupNote[] }>;
  // ── Group activity ledger (B-WP3) ───────────────────────────────────────────
  /** `group.events.list` — newest-first activity for the group (member-gated). */
  listEvents(input: { groupUuid: string; limit?: number }): Promise<{ events: GroupEvent[] }>;

  // ── Public groups + moderator role (B-WP12) ─────────────────────────────────
  /** `group.directory.list` — PUBLIC keyset-paginated, evidence-ranked directory. */
  directory(input: { limit?: number; cursor?: string | null }): Promise<{
    groups: DirectoryGroup[];
    nextCursor: string | null;
  }>;
  /** `group.publicGet` — PUBLIC read of one public group + viewer relationship. */
  publicGet(input: { groupUuid: string }): Promise<PublicGroupMeta>;
  /**
   * `group.publicJoin` — file an owner-approval join request (authed, no token).
   * `declined` is terminal on this surface: a previously-declined requester is
   * NOT re-filed (server returns `declined` without touching the row).
   */
  publicJoin(input: { groupUuid: string }): Promise<{
    groupUuid: string;
    name: string;
    status: "member" | "full" | "pending" | "declined";
  }>;
  /** `group.updateProfile` — owner sets visibility + description (lights the directory). */
  updateProfile(input: {
    groupUuid: string;
    visibility?: "private" | "public";
    description?: string;
  }): Promise<{ visibility: "private" | "public" }>;
  /** `group.moderators.list` — the moderator roster (member-readable). */
  listModerators(input: { groupUuid: string }): Promise<{ moderators: GroupModerator[] }>;
  /** `group.moderators.assign` — owner grants the moderator role to a member. */
  assignModerator(input: { groupUuid: string; userUuid: string }): Promise<void>;
  /** `group.moderators.revoke` — owner revokes the moderator role. */
  revokeModerator(input: { groupUuid: string; userUuid: string }): Promise<void>;
}

// ── Implementation ─────────────────────────────────────────────────────────────

// AppRouter now carries the `group` namespace and the `sourceGroupShare`
// spin-off arm (family-groups G-B, 2026-07-02) — the wrapper types flow
// straight off the generated client.
function groupNs(client: ApiClient) {
  return client.group;
}
function recipeNs(client: ApiClient) {
  return client.recipe;
}

/**
 * Wrap a raw oRPC client in the typed GroupsApi surface. Exported so tests can
 * pass a hand-rolled fake client, and so the caller controls the client's base
 * URL / fetch.
 */
export function wrapGroupsApi(client: ApiClient): GroupsApi {
  // Server contract deltas adapted at this seam (family-groups G-B/G-C
  // integration, 2026-07-02): mutations return `{ ok: true }` (mapped to
  // void); `role` arrives as a plain string (narrowed here — the server CHECK
  // constraint guarantees the union); `group.list` returns rows keyed by
  // `groupUuid` + a D1 `memberCount` COUNT, projected here onto GroupSummary
  // (`uuid`, dropping the wire-only `joinedAt`).
  const asRole = (role: string): GroupRole => (role === "owner" ? "owner" : "member");
  const done = (p: Promise<{ ok: true }>): Promise<void> => p.then(() => undefined);
  return {
    create: (input) => groupNs(client).create(input),
    list: async () => {
      const { groups } = await groupNs(client).list({});
      return {
        groups: groups.map((g) => ({
          uuid: g.groupUuid,
          name: g.name,
          role: asRole(g.role),
          memberCount: g.memberCount,
          // WP-B: server-computed. Narrow defensively — a client built against
          // an older server must fall back to "writable", never to a spurious
          // read-only lock the user cannot clear.
          frozen: g.frozen === true,
        })),
      };
    },
    get: async (input) => {
      const meta = await groupNs(client).get(input);
      return {
        ...meta,
        role: asRole(meta.role),
        // B-WP12 fields arrive as plain strings/arrays from the server; narrow
        // visibility to the union (server writes only 'private'|'public').
        visibility: meta.visibility === "public" ? "public" : "private",
        description: meta.description ?? null,
        moderatorUuids: meta.moderatorUuids ?? [],
        isModerator: meta.isModerator === true,
        canModerate: meta.canModerate === true,
        // WP-B: same defensive narrowing as `list` — absent ⇒ writable.
        frozen: meta.frozen === true,
        members: meta.members.map((m) => ({ ...m, role: asRole(m.role) })),
      };
    },
    rename: (input) => done(groupNs(client).rename(input)),
    remove: (input) => done(groupNs(client).delete(input)),
    leave: (input) => done(groupNs(client).leave(input)),
    removeMember: (input) => done(groupNs(client).members.remove(input)),
    mintInvite: (input) => groupNs(client).invite.mint(input),
    revokeInvite: (input) => done(groupNs(client).invite.revoke(input)),
    previewInvite: (input) => groupNs(client).invite.preview(input),
    requestToJoin: (input) => groupNs(client).join.request(input),
    listRequests: (input) => groupNs(client).requests.list(input),
    approveRequest: (input) => done(groupNs(client).requests.approve(input)),
    declineRequest: (input) => done(groupNs(client).requests.decline(input)),
    share: (input) => groupNs(client).share(input),
    unshare: (input) => done(groupNs(client).unshare(input)),
    listShares: (input) => groupNs(client).shares.list(input),
    getShare: (input) => groupNs(client).shares.get(input),
    spinOffFromGroupShare: (input) => recipeNs(client).spinOff({ sourceGroupShare: input }),
    myShares: async (input) => {
      // `group.myShares` has no `.output()` schema, so the generated type is a
      // union (empty branch → `Record<string, unknown>[]`). Project each row
      // onto the narrow MyShareRow shape at this seam.
      const res = (await groupNs(client).myShares(input)) as {
        shares?: ReadonlyArray<Record<string, unknown>>;
      };
      return {
        shares: (res.shares ?? []).map((s) => ({
          groupUuid: String(s["groupUuid"]),
          name: String(s["name"]),
          shareUuid: String(s["shareUuid"]),
          sharedAt: Number(s["sharedAt"]),
        })),
      };
    },
    publishReports: (input) => groupNs(client).reports.publish(input),
    retractReport: (input) => done(groupNs(client).reports.retract(input)),
    listReports: (input) => groupNs(client).reports.list(input),
    reportAnchors: (input) => groupNs(client).reports.anchors(input),
    addNote: (input) => groupNs(client).notes.add(input),
    deleteNote: (input) => done(groupNs(client).notes.delete(input)),
    reportNote: async (input) => {
      const { alreadyReported } = await groupNs(client).notes.report(input);
      return { alreadyReported };
    },
    listNotes: (input) => groupNs(client).notes.list(input),
    listEvents: (input) => groupNs(client).events.list(input),
    // B-WP12 — public groups + moderator role.
    directory: (input) => groupNs(client).directory.list(input),
    publicGet: (input) => groupNs(client).publicGet(input),
    publicJoin: (input) => groupNs(client).publicJoin(input),
    updateProfile: async (input) => {
      const res = await groupNs(client).updateProfile(input);
      return { visibility: res.visibility };
    },
    listModerators: (input) => groupNs(client).moderators.list(input),
    assignModerator: (input) => done(groupNs(client).moderators.assign(input)),
    revokeModerator: (input) => done(groupNs(client).moderators.revoke(input)),
  };
}

/**
 * Default factory used by the web surfaces. `baseUrl` defaults to same-origin
 * (`""`), matching every other in-app client (`createApiClient` resolves a bare
 * relative path against `window.location.origin`).
 */
export function createGroupsApi(baseUrl = ""): GroupsApi {
  return wrapGroupsApi(createApiClient({ baseUrl }));
}
